/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { isEmpty, map, some, stubTrue } from 'lodash'
import { toast } from 'react-toastify'

import ScanGiftCardGif from '../assets/gifs/gift-card.gif'

import NetworkIndicator from '../containers/NetworkIndicator'
import BackButtonChevron from '../components/BackButtonChevron'
import GratuityHeader from '../components/GratuityHeader'
import SegmentedControlV2 from '../components/segmentedControl/SegmentedControl'
import ConnectingIndicator from '../components/ConnectingIndicator'
import useCreateOrderInProgress from '../hooks/useCreateOrderInProgress'
import useGetGiftCardBalance from '../hooks/useGetGiftCardBalance'
import { getOrderInProgress } from '../selectors/order'
import { centsToDollars } from '../utils/formatters'
import PeripheralBridge from '../utils/peripheralBridge'
import { KEY_CODES } from '../kiosk/utils/constants'
import { addPaymentToOrderInProgress, clearOrderInProgress, closeNoOpTender } from '../actions/order'
import styles from './ScanScreen.module.scss'
import { makeSegmentChoices, onMountScanScreen, registerScanListeners, toastError, tokenAlreadyUsed } from '../utils/scanScreen'

const segmentChoices = makeSegmentChoices('Gift Card')

const GiftCard = ({
  className,
  onGoBack,
  needCreateOrder,
  order,
  addPaymentToOrderInProgress,
  balanceDueInCents,
  placeFreeOrder,
  push,
}) => {
  const classNames = cn(styles.scanScreen, className)
  const isMobile = window.matchMedia('(max-width: 700px)')?.matches
  const [selectedSubmissionType, setSelectedSubmissionType] = useState(isMobile ? 'entry' : 'scan')
  const [isProcessing, setIsProcessing] = useState(false)
  const [giftCardCode, setGiftCardCode] = useState('')
  const [createOrder, orderCreated] = useCreateOrderInProgress()
  const [getGiftCardBalance, { giftCardBalance, dataSucceeded, dataFailed }] = useGetGiftCardBalance()
  const [keyboardIsActive, setKeyboardIsActive] = useState(false)
  const [scanError, setScanError] = useState(false)
  const hasNoBalance = +giftCardBalance === 0

  const GiftCardRef = useRef()

  const closeKeyBoard = () => {
    if (keyboardIsActive) {
      GiftCardRef?.current?.blur()
    }
  }

  const handleFocusChange = () => {
    setTimeout(() => {
      setKeyboardIsActive(document.activeElement === GiftCardRef?.current)
    }, 150)
  }

  useEffect(() => {
    if (needCreateOrder) {
      createOrder()
    }

    return onMountScanScreen(handleFocusChange)
  }, [])

  useEffect(() => {
    if (dataFailed) {
      setIsProcessing(false)
      toastError('Error requesting card balance. Please try again.', 'md', isMobile)
    }
  }, [dataFailed])

  useEffect(() => {
    if (orderCreated && order?.amountInCents === 0) {
      placeFreeOrder()
    }
  }, [orderCreated])

  useEffect(() => {
    if (dataSucceeded) {
      handleSuccess()
    }
  }, [dataSucceeded])

  const onScanSuccess = (scannedCode) => {
    setGiftCardCode(scannedCode)
    getGiftCardBalance(scannedCode)
    setIsProcessing(true)
  }

  useEffect(() => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (selectedSubmissionType === 'entry' && GiftCardRef.current) {
      GiftCardRef.current?.focus()
      if (!bridgeInstance) return

      bridgeInstance.call('stopScanner')
    }

    if (selectedSubmissionType === 'scan') {
      if (!bridgeInstance) return

      registerScanListeners({ order, onSuccess: onScanSuccess, paymentType: 'Gift card', setSelectedSubmissionType, isMobile, setScanError })
    }
  }, [selectedSubmissionType])

  useEffect(() => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) return

    if (isProcessing || selectedSubmissionType === 'entry') {
      bridgeInstance.call('stopScanner')
    } else if (selectedSubmissionType === 'scan') {
      bridgeInstance.call('startScanner')
    }
  }, [isProcessing, selectedSubmissionType])

  const primaryText = centsToDollars(balanceDueInCents !== 0 ? balanceDueInCents : order?.amountInCents)
  const secondaryText =
    selectedSubmissionType === 'scan'
      ? 'Please scan gift card'
      : 'Please type gift card code'

  const handleSubmissionTypeChange = value => {
    if (selectedSubmissionType !== value) {
      setSelectedSubmissionType(value)
    }
  }

  const redirect = (tenderAmountInCents) => {
    push({
      pathname: '/tender-selection',
      state: {
        tenderAmountInCents,
        scanScreenType: 'gift card',
      }
    })
  }

  const handleSuccess = () => {
    if (hasNoBalance) {
        closeKeyBoard()
        toastError('Card has no funds. Please try another card or tender.', 'md', isMobile)
        setIsProcessing(false)
        return
    }

    let tenderAmountInCents
    let redirectToSplit = false
    if (giftCardBalance >= balanceDueInCents) {
      tenderAmountInCents = balanceDueInCents
    } else {
      tenderAmountInCents = giftCardBalance
      redirectToSplit = true
    }

    addPaymentToOrderInProgress({
      tenderAmountInCents,
      paymentType: 'gift_card',
      tenderDisplayName: 'Gift Card',
      token: giftCardCode,
    })

    toast.success('Scan gift card successful')
    closeKeyBoard()
    if (redirectToSplit) {
      redirect(balanceDueInCents - giftCardBalance)
    }
  }

  const handleKeyPress = e => {
    if (e.keyCode === KEY_CODES.ENTER || e.keyCode === '9' || e.key === 'ENTER' || e.key === 'Tab') {
      const isValid = /^[a-z|0-9\-]*?$/gi.test(giftCardCode);
      if (!isValid) {
        toastError('The code entered contains invalid characters', 'md', isMobile)
        return
      }
      if (tokenAlreadyUsed(giftCardCode, order)) {
        toastError('Gift card already entered and applied to order', 'md', isMobile)
        return
      }
      getGiftCardBalance(giftCardCode)
      setIsProcessing(true)
    }
  }

  const handleTicketCodeChange = e => {
    setGiftCardCode(e?.target?.value)
  }

  if (!orderCreated && needCreateOrder) {
    return <ConnectingIndicator />
  }

  const chooseTenderOptionButton = 
    !isProcessing &&
    !keyboardIsActive && 
    (dataFailed || hasNoBalance || scanError) && (
      <div
        className={styles.chooseTenderOption}
        onClick={() => {
          redirect(balanceDueInCents)
        }}
      >
        Pay With Other Method
      </div>
    )

  return (
    <div className={classNames} >
      <div className={styles.goBack}>
        <BackButtonChevron
          onClick={() => {
            closeKeyBoard()
            setTimeout(() => {
              onGoBack()
            }, 200)
            }
          }
          backButtonText={isMobile ? '' : 'Go Back'}
        />
        <span className={styles.mobileTitle}>{primaryText}</span>
        <div className={styles.contentRight}>
          <NetworkIndicator />
        </div>
      </div>
      <div className={styles.scanScreen__header}>
        <GratuityHeader
          className={styles.gratuityHeader}
          primaryText={isMobile ? '' : primaryText}
          secondaryText={secondaryText}
        />
      </div>
      {!isMobile && <div className={styles.scanScreen__submissionChoices}>
        <SegmentedControlV2
          className={styles.segmentedControl}
          items={segmentChoices}
          selectedValue={selectedSubmissionType}
          onSelect={handleSubmissionTypeChange}
        />
      </div>}
      <div className={styles.scanScreen__body}>
        <div className={styles.choicesContent}>
          <div
            className={cn(styles.option, {
              [styles.isHidden]:
                selectedSubmissionType === 'entry' || isProcessing
            })}
          >
            <img src={ScanGiftCardGif} alt={"Scan Gift Card"} className={styles.qrImage}/>
          </div>
          <div
            className={cn(styles.option, {
              [styles.isHidden]:
                selectedSubmissionType === 'scan' || isProcessing
            })}
          >
            <input
              className={styles.manualEntry}
              ref={GiftCardRef}
              value={giftCardCode}
              onChange={handleTicketCodeChange}
              onKeyDown={handleKeyPress}
            />
          </div>
          <div
            className={cn(styles.processing, {
              [styles.isHidden]: !isProcessing
            })}
          >
            <div className={styles.loadingSpinner}>
              <ConnectingIndicator position={'initial'}/>
            </div>
            <span className={styles.topText}>Processing...</span>
            <span className={styles.bottomText}>please wait.</span>
          </div>
        </div>
        {chooseTenderOptionButton}
      </div>
    </div>
  )
}

const mapStateToProps = (state, props) => {
  const order = getOrderInProgress(state)
  const balanceDueInCents = order?.balanceDueInCents
  const push = props?.history?.push ?? (() => {})

  return {
    order,
    needCreateOrder: isEmpty(order),
    balanceDueInCents,
    push,
  }
}

const mapDispatchToProps = (dispatch, props) => {
  const goBack = props?.history?.goBack ?? (() => {})

  return {
    onGoBack: () => {
      dispatch(clearOrderInProgress())
      goBack()
    },
    addPaymentToOrderInProgress: payment =>
      dispatch(addPaymentToOrderInProgress(payment)),
    placeFreeOrder: () => {
      dispatch(closeNoOpTender())
    },
  }
}
export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(GiftCard)
)
